import React from "react";
import SlideOverPanel from "../SlideOverPanel";
import GlobalTimeNavigatorBar from "../GlobalTimeNavigatorBar";
import OpenClosedFilterDropdown from "../OpenClosedFilterDropdown";
import PageViewPreferenceSaveButton from "../PageViewPreferenceSaveButton";
import AdvancedFilterFieldsForm from "./AdvancedFilterFieldsForm";
import "./AdvancedFilterFieldsForm.css";
import "./ListFiltersSlideOver.css";

/**
 * Labeled stacked control used inside the list filter slide-over.
 */
function ListFilterField({ label, children }) {
  return (
    <div className="list-filters-slide-over__field">
      <span className="list-filters-slide-over__field-label">{label}</span>
      <div className="list-filters-slide-over__field-control">{children}</div>
    </div>
  );
}

/**
 * When Status is enabled, keyword (text) fields stay above it and other criteria stay below.
 * Without Status, fields render in definition order.
 */
function partitionFilterFields(fields, showStatus) {
  if (!showStatus) return { leadingFields: [], criteriaFields: fields };
  return {
    leadingFields: fields.filter((field) => field.type === "text"),
    criteriaFields: fields.filter((field) => field.type !== "text"),
  };
}

function timeRangeControlProps(timeRange) {
  return {
    title: timeRange.title,
    view: timeRange.view,
    onViewChange: timeRange.onViewChange || timeRange.setView,
    onToday: timeRange.onToday || timeRange.goToday,
    onPrev: timeRange.onPrev || timeRange.movePrev,
    onNext: timeRange.onNext || timeRange.moveNext,
    viewOptions: timeRange.viewOptions,
  };
}

/**
 * Platform list filter panel.
 * Field changes apply immediately. Done closes. Clear filter resets the panel.
 *
 * Optional slots (omit to hide; Task Management enables both):
 * - status: { value, onChange, includeExpired?, label? } — Open / Closed (/ Expired)
 * - timeRange: useGlobalTimeNavigator() result, or { title, view, onViewChange, onToday, onPrev, onNext, viewOptions?, label? }
 */
export default function ListFiltersSlideOver({
  open,
  onClose,
  title = "Filters",
  subtitle,
  width = 400,
  fields,
  values = {},
  onFieldChange,
  disabled = false,
  hint = "Leave fields blank to show all results.",
  status,
  timeRange,
  onClear,
  clearLabel = "Clear filter",
  pageViewPreferenceProps,
  children,
}) {
  const fieldList = Array.isArray(fields) ? fields.filter(Boolean) : [];
  const showStatus = !!(status && typeof status.onChange === "function");
  const timeControl = timeRange ? timeRangeControlProps(timeRange) : null;
  const showTimeRange = !!(timeControl && typeof timeControl.onViewChange === "function");
  const { leadingFields, criteriaFields } = partitionFilterFields(fieldList, showStatus);

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width={width}
      footer={
        <>
          {pageViewPreferenceProps ? (
            <PageViewPreferenceSaveButton {...pageViewPreferenceProps} />
          ) : null}
          <div className="list-filters-slide-over__footer-actions">
            {onClear ? (
              <button type="button" className="btn btn-md btn-secondary" onClick={onClear}>
                {clearLabel}
              </button>
            ) : null}
            <button type="button" className="btn btn-md btn-primary" onClick={onClose}>
              Done
            </button>
          </div>
        </>
      }
      footerClassName="list-filters-slide-over__footer"
    >
      <div className="list-filters-slide-over__sections">
        {leadingFields.length > 0 ? (
          <AdvancedFilterFieldsForm
            fields={leadingFields}
            values={values}
            onFieldChange={onFieldChange}
            disabled={disabled}
          />
        ) : null}

        {showStatus ? (
          <ListFilterField label={status.label || "Status"}>
            <OpenClosedFilterDropdown
              value={status.value}
              onChange={status.onChange}
              label=""
              includeExpired={!!status.includeExpired}
            />
          </ListFilterField>
        ) : null}

        {criteriaFields.length > 0 ? (
          <div className="list-filters-slide-over__criteria">
            <AdvancedFilterFieldsForm
              fields={criteriaFields}
              values={values}
              onFieldChange={onFieldChange}
              disabled={disabled}
            />
            {hint ? <p className="advanced-filter-fields-form__hint">{hint}</p> : null}
          </div>
        ) : leadingFields.length > 0 && hint ? (
          <p className="advanced-filter-fields-form__hint">{hint}</p>
        ) : null}

        {showTimeRange ? (
          <ListFilterField label={timeRange.label || "Time range"}>
            <GlobalTimeNavigatorBar variant="stacked" {...timeControl} />
          </ListFilterField>
        ) : null}

        {children}
      </div>
    </SlideOverPanel>
  );
}
